/**
 * Roolit ja oikeudet (suunnitelma 2.1–2.2).
 *
 * Tämä tiedosto on oikeusmatriisin ainoa esitys koodissa. Käyttöliittymä
 * piilottaa toimintoja tämän perusteella, mutta se on käytettävyyttä —
 * palvelin tarkistaa saman asian jokaisessa päätteessä.
 *
 * Roolien näyttönimet eivät ole täällä vaan käyttöliittymän sanastossa
 * (`apps/web/src/lib/i18n`): ne ovat käännettävää tekstiä, ja tämä
 * tiedosto on kielestä riippumaton.
 *
 * Huomaa mitä tämä tiedosto EI ratkaise: euromäärien näkyvyys ei ole pelkkä
 * roolikysymys, koska asiakasvastaava näkee eurot vain vastuuasiakkaistaan.
 * Se on näkyvyysalue, ja se asuu backendissä (suunnitelma 13.4).
 */
export const ROLES = ['ADMIN', 'USER', 'ACCOUNT_MANAGER', 'REPORTER'];
/**
 * Oikeusmatriisi. Vasemmalla toiminto, oikealla roolit joilla se on.
 * Vastaa suunnitelman lukua 2.2 rivi riviltä.
 */
export const CAPABILITIES = {
    /** Omien tuntien kirjaus, muokkaus ja poisto. */
    logOwnTime: ['ADMIN', 'USER', 'ACCOUNT_MANAGER'],
    /** Toisten kirjausten katselu (näkyvyysalueen rajaamana). */
    viewOthersEntries: ['ADMIN', 'ACCOUNT_MANAGER', 'REPORTER'],
    /** Toisten kirjausten muokkaus. */
    editOthersEntries: ['ADMIN'],
    /** Perustietojen hallinta: asiakkaat, tehtävälajit, projektit, hinnat. */
    manageMasterData: ['ADMIN'],
    /** Asiakasvastuiden hallinta. */
    manageAssignments: ['ADMIN'],
    /** Käyttäjät ja roolit. */
    manageUsers: ['ADMIN'],
    /** Järjestelmäasetukset ja API-tunnukset. */
    manageSettings: ['ADMIN'],
    /** Kuukauden lukitus ja avaus. */
    lockMonths: ['ADMIN'],
    /** Lukitushistorian katselu perusteluineen. */
    viewLockHistory: ['ADMIN', 'ACCOUNT_MANAGER', 'REPORTER'],
    /** Jaksoraportti ja porautuminen. */
    viewReports: ['ADMIN', 'ACCOUNT_MANAGER', 'REPORTER'],
    /** Vienti CSV / JSON. */
    exportEntries: ['ADMIN', 'ACCOUNT_MANAGER', 'REPORTER'],
    /** Asiakkaan kuukauden kuittaus tarkistetuksi. */
    checkCustomerMonth: ['ADMIN', 'ACCOUNT_MANAGER'],
    /**
     * Huomautuksen kirjoittaminen toisen kirjauksesta (backlog 3.5).
     *
     * Sama joukko kuin kuittauksella, ja samasta syystä: tarkistaja saa
     * kertoa tekijälle, mikä näyttää väärältä. REPORTER ei — se on
     * tarkoituksella *pelkkä* katselija, eikä huomautus ole katselua vaan
     * viesti, joka lähtee toiselle ihmiselle.
     *
     * Huomautus ei ole muokkausoikeus eikä hyväksyntä: korjauksen tekee
     * tekijä, ja huomautus on vain pyyntö tehdä se.
     */
    noteOthersEntries: ['ADMIN', 'ACCOUNT_MANAGER'],
    /**
     * Työntekijäkohtainen erittely ja laskutustavoite (12.6).
     *
     * Vain ADMIN — kapeampi kuin `viewReports`, ja se on tarkoitus. Raportoija
     * ja asiakasvastaava näkevät eurot, koska ne ovat laskutusaineistoa.
     * Henkilön laskutusaste tavoitteeseen verrattuna on eri asia: se on työn
     * arviointia, ja sen näkeminen on esihenkilön eikä katsojan oikeus.
     */
    viewEmployeeReport: ['ADMIN'],
    /**
     * Koulutusten kohdennukset ja asetukset (docs/KOULUTUKSET.md).
     *
     * Vain ADMIN, ja kapeammin kuin `manageMasterData` antaisi ymmärtää:
     * kohdennus on päätös siitä, kenen on pakko käyttää työaikaansa
     * koulutukseen, ja se on työnantajan päätös eikä perustietojen
     * ylläpitoa.
     */
    manageTrainings: ['ADMIN'],
    /**
     * Muiden koulutustila: matriisi, sen vienti ja yksittäisen työntekijän
     * suoritushistoria.
     *
     * Sama rajaus ja sama peruste kuin työntekijäerittelyllä (12.6):
     * henkilön koulutustila on esihenkilön tietoa, ei katsojan. Raportoija
     * näkee laskutusaineiston, koska se on asiakkaan asia — se, kuka on
     * jättänyt tietosuojakoulutuksen käymättä, ei ole.
     */
    viewTrainingReports: ['ADMIN'],
    /** Takautuva hintalaskenta. */
    recalculateRates: ['ADMIN'],
    /**
     * Projektinhallinnan taulut (docs/PROJEKTINHALLINTA.md, luku 9).
     *
     * Rooli avaa moduulin, jäsenyys avaa taulun: USER ja ACCOUNT_MANAGER
     * näkevät niiden asiakkuuksien taulut, joihin heidät on liitetty, ADMIN
     * kaikki. REPORTER ei ole mukana — se on laskutusaineiston katsoja, ja
     * työjono ei ole laskutusaineistoa. Rajaus asiakkuuksiin on
     * näkyvyysalueen asia ja asuu backendissä (`visibility.ts`), kuten
     * eurojenkin.
     */
    useBoards: ['ADMIN', 'USER', 'ACCOUNT_MANAGER'],
    /**
     * Uimaradan poisto sekä toisten tehtävien muokkaus ja poisto.
     *
     * ACCOUNT_MANAGER vain omissa asiakkuuksissaan — sama rajaus kuin
     * huomautuksilla. Tavallinen jäsen saa siirtää ja monistaa kenen tahansa
     * tehtävän, muttei muuttaa sen sisältöä: siirto on työn aloittamista,
     * muokkaus on toisen tekstin muuttamista.
     */
    moderateBoards: ['ADMIN', 'ACCOUNT_MANAGER'],
    /**
     * Asiakkaan kuvaus, yhteyshenkilöt ja dokumenttilinkit.
     *
     * Tämä on ensimmäinen kirjoitusoikeus asiakasriviin, joka ei ole
     * ylläpitäjän yksin — ja siksi se on oma oikeutensa eikä
     * `manageMasterData`n laajennus. Hinnat, asiakaskoodit ja tehtävälajit
     * ovat laskutuksen perustietoa ja pysyvät ADMINilla; yhteyshenkilö ja
     * sopimuslinkki ovat asiakkuuden hoitamisen tietoa, ja ne tietää se,
     * joka asiakkuutta hoitaa.
     *
     * Rooli avaa oikeuden, asiakasvastuu rajaa sen: asiakasvastaava muokkaa
     * vain vastuuasiakkaitaan. Rajaus on `visibility.ts`:ssä
     * (`canManageCustomerDetails`), koska se ei ole pelkkä roolikysymys.
     */
    manageCustomerDetails: ['ADMIN', 'ACCOUNT_MANAGER'],
    /**
     * Kumppanirekisteri (PROJEKTINHALLINTA 29): kumppanit ja niiden
     * yhteyshenkilöt, joille tehtävän voi osoittaa. Samat roolit kuin
     * asiakkaan tiedoilla, mutta ilman asiakasvastuun rajausta: kumppani on
     * yhteinen, ei yhden asiakkuuden. Luku on kaikkien taulujen käyttäjien.
     */
    managePartners: ['ADMIN', 'ACCOUNT_MANAGER'],
    /**
     * Kehitys- ja osaamismoduuli auki (docs/KEHITYSSUUNNITELMAT.md, luku 10).
     *
     * Oma suunnitelma, omat sertifikaatit — ja jos henkilöllä on alaisia,
     * heidän vastaavansa. REPORTER ei ole mukana: se on laskutusaineiston
     * katsoja, ja ihmisen kehityskeskustelu on kauimpana laskutusaineistosta,
     * mitä tässä järjestelmässä on.
     *
     * Rooli avaa moduulin, **esihenkilösuhde avaa toisen ihmisen tiedot** —
     * sama kuvio kuin taulujen jäsenyydellä. Rajaus asuu `visibility.ts`:ssä
     * (`canViewDevelopment`), koska se ei ole roolikysymys.
     */
    useDevelopment: ['ADMIN', 'USER', 'ACCOUNT_MANAGER'],
    /**
     * Sertifikaattiluettelo: mitä talossa ylipäätään voi saavuttaa.
     *
     * Vain ADMIN, vaikka tarve syntyy esihenkilön kirjatessa saavutusta.
     * Luettelo on kymmeniä rivejä vuodessa, ja sen hajoaminen — `AZ-104`,
     * `AZ104`, `Azure Administrator` erikseen laskettuina — veisi
     * rekisteriltä sen ainoan arvon, jonka takia se on olemassa.
     */
    manageCertifications: ['ADMIN'],
    /**
     * Osaamisrekisterin matriisi ja sen vienti.
     *
     * Vain ADMIN. Asiakasvastaavaa harkittiin, koska raportti on
     * tarjousliitteen aineistoa ja tarjouksen kirjoittaa usein hän — mutta
     * talossa tarjoukset kirjoittaa ylläpitäjä (päätös 15.9.2026), eikä
     * oikeutta anneta varalta. Sama rajaus ja sama peruste kuin
     * koulutusmatriisilla ja työntekijäerittelyllä.
     */
    viewCertificationReport: ['ADMIN'],
    /**
     * Hallinnan yleiskatsaus kehityskeskusteluihin
     * (docs/KEHITYSSUUNNITELMAT.md, luku 10).
     *
     * Vain ADMIN. Näkymä kertoo, **mitkä esihenkilöt eivät pidä
     * keskustelujaan** — ja se on eri tieto kuin oman tiimin tilanne, joka
     * näkyy esihenkilölle itselleen ilman tätä oikeutta.
     *
     * Näkymässä ei ole keskustelujen sisältöä: nimet, päivät ja luvut.
     * Sisältö on esihenkilön ja työntekijän välistä, eikä ylläpito ole
     * jokaisen keskustelun osapuoli.
     */
    viewDevelopmentOverview: ['ADMIN'],
    /** Tuonti-API. */
    importData: ['ADMIN'],
    /**
     * Salaisuuksien jako (docs/SALAISUUDET.md, luku 11): luonti, omat
     * salaisuudet lukutietoineen ja niiden peruminen.
     *
     * Asiakasvastuu ei ole ehto. Salaisuutta ei sidota asiakkaaseen, ja
     * asiakasvastaava jakaa tunnuksia myös asiakkaille, joiden vastuuhenkilöitä
     * on useampi.
     */
    shareSecrets: ['ADMIN', 'ACCOUNT_MANAGER'],
    /**
     * Kaikkien salaisuuksien metatiedot ja peruminen — **ei koskaan
     * sisältöä**, jota palvelin ei voisi purkaa ilman linkin avainta.
     * Tarkoitus on, että lähtevän työntekijän avoimet jaot voi tarkistaa.
     */
    manageAllSecrets: ['ADMIN'],
};
export function can(role, capability) {
    return CAPABILITIES[capability].includes(role);
}
/**
 * Roolit, jotka API-tunnukselle voi antaa (suunnitelma 9.5).
 *
 * Vain ne, joiden näkyvyys ei riipu asiakasvastuista. Tunnuksella ei ole
 * eikä voi olla vastuita — vastuu on ihmisen ja asiakkaan välinen suhde —
 * joten asiakasvastaavan tunnus näkisi tyhjän maailman, ja tavallisen
 * käyttäjän tunnus vain tunnuksen luojan omat kirjaukset. Kumpikin
 * näyttäisi rikkinäiseltä eikä olisi se, mitä tunnuksella haettiin.
 */
export const API_TOKEN_ROLES = ['ADMIN', 'REPORTER'];
/**
 * Tunnuksen käyttötarkoitus (backlog 5).
 *
 * `IMPORT` on luvun 9.5 alkuperäinen tunnus: palvelutunnus, joka ajaa
 * migraation ja lukee rajapintaa. Sillä ei ole ihmistä takanaan siinä
 * mielessä, että se toimisi jonkun puolesta — se toimii järjestelmän
 * puolesta.
 *
 * `AGENT` on henkilökohtainen: se toimii **luojansa puolesta**, näkee vain
 * hänen kirjauksensa ja vain lähipäiviltä, eikä pääse muualle kuin
 * MCP-päätteeseen. Ero ei ole hienosäätöä vaan koko turvamalli: agentin
 * tunnus, joka pääsisi tuontipäätteeseen, kirjoittaisi laskutusrivejä
 * ohi ehdotusmallin.
 */
export const API_TOKEN_SCOPES = ['IMPORT', 'AGENT'];
/**
 * Näkeekö rooli lähtökohtaisesti muitakin kuin omia kirjauksiaan.
 * ACCOUNT_MANAGER näkee — mutta vain vastuuasiakkaistaan, ja sen rajauksen
 * tekee näkyvyysalue, ei tämä funktio.
 */
export function hasAnyCrossUserVisibility(role) {
    return can(role, 'viewOthersEntries');
}
//# sourceMappingURL=roles.js.map